import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Button,
  Chip,
  Badge,
  AppBar,
  Toolbar,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  Paper,
  Divider
} from '@mui/material';
import {
  NotificationImportant as NotificationIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  RoomService,
  Edit as EditIcon,
  Help as HelpIcon,
  Receipt as ReceiptIcon,
  NotificationImportant as BellIcon,
  Message as MessageIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { customerRequestApi, stewardApi, timelineApi } from '../api/api';
import type { 
  CustomerRequest, 
  StewardPerformance,
  NotificationEvent
} from '../types/notification.types';
import { 
  RequestType, 
  RequestStatus,
  REQUEST_TYPE_LABELS,
  EventType
} from '../types/notification.types';
import { useAuth } from '../context/AuthContext';
import { useSSE } from '../services/sseService';
import { AppIconButton } from './ui';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`steward-tabpanel-${index}`}
      aria-labelledby={`steward-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

const REQUEST_TYPE_ICONS: Record<RequestType, React.ReactElement> = {
  [RequestType.TABLE_SERVICE]: <RoomService />,
  [RequestType.ORDER_MODIFICATION]: <EditIcon />,
  [RequestType.ASSISTANCE]: <HelpIcon />,
  [RequestType.BILL_REQUEST]: <ReceiptIcon />,
  [RequestType.CALL_STEWARD]: <BellIcon />,
  [RequestType.CUSTOM_MESSAGE]: <MessageIcon />
};

interface StewardNotificationDashboardProps {
  vendorId: number;
}

export const StewardNotificationDashboard: React.FC<StewardNotificationDashboardProps> = ({ vendorId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  
  const [activeTab, setActiveTab] = useState(0);
  const [activeRequests, setActiveRequests] = useState<CustomerRequest[]>([]);
  const [myRequests, setMyRequests] = useState<CustomerRequest[]>([]);
  const [recentEvents, setRecentEvents] = useState<NotificationEvent[]>([]);
  const [performance, setPerformance] = useState<StewardPerformance[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshKey(k => k + 1);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Connect to SSE for real-time updates
  useSSE({
    endpoint: 'vendor',
    id: vendorId,
    onEvent: (type, data) => {
      console.log('[StewardDashboard] SSE event:', type, data);
      
      // Refresh dashboard when customer request events are received
      if (type === 'customer-request' || type === 'event') {
        setRefreshKey(k => k + 1);
      }
    },
    onConnect: () => {
      console.log('[StewardDashboard] SSE connected');
    },
    onError: (error) => {
      console.error('[StewardDashboard] SSE error:', error);
    }
  });

  // Fetch active requests
  useEffect(() => {
    const fetchActiveRequests = async () => {
      try {
        const response = await customerRequestApi.getActiveByVendor(vendorId);
        setActiveRequests(response.data);
      } catch (err: any) {
        console.error('Failed to fetch active requests:', err);
      }
    };

    fetchActiveRequests();
  }, [vendorId, refreshKey]);

  // Fetch my claimed requests
  useEffect(() => {
    const fetchMyRequests = async () => {
      if (!user) return;
      
      try {
        const response = await stewardApi.getMyRequests();
        setMyRequests(response.data);
      } catch (err: any) {
        console.error('Failed to fetch my requests:', err);
      }
    };

    fetchMyRequests();
  }, [user, refreshKey]);

  // Fetch recent events
  useEffect(() => {
    const fetchRecentEvents = async () => {
      try {
        const response = await timelineApi.getVendorTimeline(vendorId, {
          eventType: EventType.STEWARD_ACTION,
          size: 10
        });
        setRecentEvents(response.data.content || []);
      } catch (err: any) {
        console.error('Failed to fetch recent events:', err);
      }
    };

    fetchRecentEvents();
  }, [vendorId, refreshKey]);

  // Fetch performance metrics
  useEffect(() => {
    const fetchPerformance = async () => {
      try {
        const response = await stewardApi.getPerformance(vendorId, 7);
        setPerformance(response.data);
      } catch (err: any) {
        console.error('Failed to fetch performance:', err);
      }
    };

    fetchPerformance();
  }, [vendorId, refreshKey]);

  const handleClaimRequest = async (requestId: number) => {
    setLoading(true);
    setError(null);

    try {
      await stewardApi.claimRequest(requestId);
      setSuccess('Request claimed successfully!');
      setRefreshKey(k => k + 1);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to claim request');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteRequest = async (requestId: number, notes?: string) => {
    setLoading(true);
    setError(null);

    try {
      await stewardApi.completeRequest(requestId, notes);
      setSuccess('Request completed successfully!');
      setRefreshKey(k => k + 1);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to complete request');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const pendingRequests = activeRequests.filter(r => r.status === RequestStatus.PENDING);
  const claimedRequests = activeRequests.filter(r => r.status === RequestStatus.CLAIMED);

  const getStatusColor = (status: RequestStatus) => {
    switch (status) {
      case RequestStatus.PENDING:
        return 'warning';
      case RequestStatus.CLAIMED:
        return 'info';
      case RequestStatus.COMPLETED:
        return 'success';
      default:
        return 'default';
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <Box sx={{ width: '100%' }}>
      <AppBar position="static" color="default">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Steward Dashboard
          </Typography>
          <Badge badgeContent={pendingRequests.length} color="error" sx={{ mr: 2 }}>
            <NotificationIcon />
          </Badge>
          <AppIconButton
            icon={<RefreshIcon />}
            tooltip="Refresh"
            variant="ghost"
            onClick={() => setRefreshKey(k => k + 1)}
            sx={{ color: 'inherit' }}
          />
        </Toolbar>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons={isMobile ? "auto" : false}
        >
          <Tab label={`Active (${pendingRequests.length})`} />
          <Tab label={`My Tasks (${myRequests.length})`} />
          <Tab label="History" />
          <Tab label="Performance" />
        </Tabs>
      </AppBar>

      {/* Active Requests Tab */}
      <TabPanel value={activeTab} index={0}>
        {pendingRequests.length === 0 ? (
          <Alert severity="info">No pending requests at the moment</Alert>
        ) : (
          <List>
            {pendingRequests.map((request) => (
              <Card key={request.id} sx={{ mb: 2 }}>
                <CardContent>
                  <ListItem disablePadding>
                    <ListItemIcon>
                      {REQUEST_TYPE_ICONS[request.requestType]}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle1">
                            {REQUEST_TYPE_LABELS[request.requestType]}
                          </Typography>
                          <Chip
                            label={`Table ${request.tableName}`}
                            size="small"
                            color="primary"
                          />
                          <Typography variant="caption" color="text.secondary">
                            {formatTime(request.createdAt)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {request.message}
                          </Typography>
                          {request.customerName && (
                            <Typography variant="caption" color="text.secondary">
                              Customer: {request.customerName} {request.customerPhone && `(${request.customerPhone})`}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleClaimRequest(request.id)}
                        disabled={loading}
                        startIcon={loading && <CircularProgress size={16} />}
                      >
                        Claim
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                </CardContent>
              </Card>
            ))}
          </List>
        )}
      </TabPanel>

      {/* My Tasks Tab */}
      <TabPanel value={activeTab} index={1}>
        {myRequests.length === 0 ? (
          <Alert severity="info">You haven't claimed any requests yet</Alert>
        ) : (
          <List>
            {myRequests.map((request) => (
              <Card key={request.id} sx={{ mb: 2 }}>
                <CardContent>
                  <ListItem disablePadding>
                    <ListItemIcon>
                      {REQUEST_TYPE_ICONS[request.requestType]}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle1">
                            {REQUEST_TYPE_LABELS[request.requestType]}
                          </Typography>
                          <Chip
                            label={`Table ${request.tableName}`}
                            size="small"
                            color="primary"
                          />
                          <Chip
                            label={request.status}
                            size="small"
                            color={getStatusColor(request.status)}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {request.message}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Claimed {formatTime(request.claimedAt!)}
                          </Typography>
                        </Box>
                      }
                    />
                    {request.status === RequestStatus.CLAIMED && (
                      <ListItemSecondaryAction>
                        <Button
                          variant="contained"
                          color="success"
                          onClick={() => handleCompleteRequest(request.id)}
                          disabled={loading}
                          startIcon={loading ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                        >
                          Complete
                        </Button>
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                </CardContent>
              </Card>
            ))}
          </List>
        )}
      </TabPanel>

      {/* History Tab */}
      <TabPanel value={activeTab} index={2}>
        <List>
          {recentEvents.map((event) => (
            <ListItem key={event.id} divider>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText
                primary={event.title}
                secondary={
                  <Box>
                    <Typography variant="body2">{event.description}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatTime(event.createdAt)}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </TabPanel>

      {/* Performance Tab */}
      <TabPanel value={activeTab} index={3}>
        <Paper elevation={0} sx={{ p: 2, mb: 2, backgroundColor: 'action.hover' }}>
          <Typography variant="h6">Last 7 Days Performance</Typography>
        </Paper>
        {performance.length === 0 ? (
          <Alert severity="info">No performance data available</Alert>
        ) : (
          <List>
            {performance.map((steward) => (
              <ListItem key={steward.stewardId} divider>
                <ListItemText
                  primary={steward.stewardName}
                  secondary={`Completed: ${steward.completedCount} requests`}
                />
                <Chip 
                  label={`${steward.completedCount}`}
                  color="primary"
                  variant="outlined"
                />
              </ListItem>
            ))}
          </List>
        )}
      </TabPanel>

      {/* Success/Error Snackbars */}
      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};